// src/pages/admin/ManageClubs.tsx
import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import { useNavigate } from "react-router-dom";
import { getClubRankingSnapshot } from "../../firebase/rankings";
import {
  getAllClubs,
  addClub,
  uploadClubImage,
  updateClubImageUrl,
  updateClubName,
  syncMissingClubs,
} from "../../firebase/clubs";
import { useTranslation } from "../../hooks/useTranslation";
import { C, FONT } from "../../theme";
import { useAuth } from "../../context/AuthContext";

const ManageClubs = () => {
  const { t } = useTranslation();
  const { userRole } = useAuth();
  const canManageClubs = userRole === "admin";
  const [clubs, setClubs] = useState<any[]>([]);
  const [newClubName, setNewClubName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState("");

  // Edit state
  const [editingClubId, setEditingClubId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  const [renameNotice, setRenameNotice] = useState("");
  const [syncingClubs, setSyncingClubs] = useState(false);
  const [syncResult, setSyncResult] = useState<string[] | null>(null);

  const [clubRankingMap, setClubRankingMap] = useState<Record<string, number>>({});
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    loadClubs();
    loadClubRankingCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClubRankingCounts = async () => {
    try {
      const snapshot = await getClubRankingSnapshot(new Date().getFullYear());
      if (snapshot?.clubs) {
        const map: Record<string, number> = {};
        snapshot.clubs.forEach((c: any) => {
          map[c.clubName] = c.athleteCount;
        });
        setClubRankingMap(map);
      }
    } catch {
      // Silently fail if no snapshot exists
    }
  };

  // Duplicate detection
  const duplicateNames = clubs
    .map((c: any) => c.name)
    .filter((name: string, i: number, arr: string[]) => arr.indexOf(name) !== i);
  const hasDuplicates = duplicateNames.length > 0;

  const loadClubs = async () => {
    setLoading(true);
    try {
      const data = await getAllClubs();
      setClubs(data);
    } catch (err) {
      setError(t("admin.clubs.error.load"));
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClubName.trim()) return;
    setSaving(true);
    setError("");
    try {
      const docRef = await addClub({ name: newClubName.trim() });
      const newClub = { id: docRef.id, name: newClubName.trim() };
      setClubs((prev) => [...prev, newClub]);
      setNewClubName("");
    } catch (err) {
      setError(t("admin.clubs.error.add"));
    } finally {
      setSaving(false);
    }
  };

  const handleEditStart = (club: any) => {
    setEditingClubId(club.id);
    setEditingName(club.name);
    setError("");
  };

  const handleEditCancel = () => {
    setEditingClubId(null);
    setEditingName("");
  };

  const handleEditSave = async (clubId: string) => {
    if (!editingName.trim()) return;
    setEditSaving(true);
    setError("");
    try {
      // Capture previous name before the update
      const previousName = clubs.find((c) => c.id === clubId)?.name ?? "";
      await updateClubName(clubId, editingName.trim());

      if (previousName && previousName !== editingName.trim()) {
        setRenameNotice(t("admin.clubs.renamePreserved"));
      }

      setEditingClubId(null);
      setEditingName("");
      await loadClubs();
    } catch (err) {
      setError(t("admin.clubs.error.edit"));
    } finally {
      setEditSaving(false);
    }
  };

  const handleSyncMissingClubs = async () => {
    setSyncingClubs(true);
    setSyncResult(null);
    try {
      const created = await syncMissingClubs();
      setSyncResult(created);
      if (created.length > 0) await loadClubs();
    } catch (err) {
      setError("Failed to sync missing clubs.");
    } finally {
      setSyncingClubs(false);
    }
  };

  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={s.container}>
        <div style={s.header}>
          <div style={s.pageLabel}>{t("admin.clubs.pageLabel")}</div>
          <div style={s.title}>{t("admin.clubs.title")}</div>
          <div style={s.subtitle}>
            {clubs.length} {t("admin.clubs.subtitle")}
          </div>
        </div>

        {canManageClubs ? <div style={s.card}>
          <div style={s.cardAccent} />
          <div style={s.cardBody}>
            <div style={s.cardTitle}>{t("admin.clubs.addTitle")}</div>
            <form onSubmit={handleAdd} style={s.addForm}>
              <input
                value={newClubName}
                onChange={(e) => setNewClubName(e.target.value)}
                placeholder={t("admin.clubs.addPlaceholder")}
                required
                style={{
                  ...s.input,
                  ...(focusedField === "club" ? s.inputFocused : {}),
                }}
                onFocus={() => setFocusedField("club")}
                onBlur={() => setFocusedField("")}
              />
              <button type="submit" style={s.primaryBtn} disabled={saving}>
                {saving ? "Adding..." : t("admin.clubs.addButton")}
              </button>
            </form>
            {error && <div style={s.errorMsg}>{error}</div>}
          </div>
        </div> : <div style={s.readOnlyNotice}>{t("admin.clubs.viewOnly")}</div>}

        {/* Sync missing clubs */}
        {canManageClubs && <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            style={{ ...s.secondaryBtn, fontSize: "12px", padding: "8px 14px" }}
            onClick={handleSyncMissingClubs}
            disabled={syncingClubs}
          >
            {syncingClubs ? t("admin.clubs.syncing") : t("admin.clubs.syncMissing")}
          </button>
          {syncResult !== null && syncResult.length === 0 && (
            <span style={{ fontSize: "12px", color: C.text3 }}>{t("admin.clubs.allSynced")}</span>
          )}
          {syncResult !== null && syncResult.length > 0 && (
            <span style={{ fontSize: "12px", color: C.success }}>
              ✓ {t("admin.clubs.syncAdded").replace("{count}", String(syncResult.length)).replace("{names}", syncResult.join(", "))}
            </span>
          )}
        </div>}

        {renameNotice && <div style={s.renameNotice}>{renameNotice}</div>}

        {/* Quick search */}
        <div style={{ marginBottom: "12px" }}>
          <input
            style={s.input}
            placeholder={t("admin.clubs.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={s.card}>
          <div style={s.cardAccent} />
          <div style={s.cardBody}>
            <div style={s.cardTitle}>
              {t("admin.clubs.listTitle")}
              <span style={s.clubCount}>{clubs.length}</span>
            </div>

            {loading ? (
              <div style={s.stateMsg}>{t("admin.clubs.loading")}</div>
            ) : clubs.length === 0 ? (
              <div style={s.emptyState}>
                <div style={s.emptyIcon}>🏫</div>
                <div>{t("admin.clubs.empty")}</div>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))",
                gap: "8px",
              }}>
                {clubs
                  .filter((club: any) =>
                    search.trim() === "" ||
                    club.name.toLowerCase().includes(search.toLowerCase())
                  )
                  .map((club, index) => (
                  <div key={club.id} style={s.listItem}>
                    <div style={s.listItemLeft}>
                      <div style={s.listIndex}>{String(index + 1).padStart(2, "0")}</div>

                      {editingClubId === club.id ? (
                        /* ── Inline edit mode ── */
                        <div style={s.editRow}>
                          <input
                            style={s.editInput}
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleEditSave(club.id);
                              if (e.key === "Escape") handleEditCancel();
                            }}
                            autoFocus
                          />
                          <button
                            style={s.saveEditBtn}
                            onClick={() => handleEditSave(club.id)}
                            disabled={editSaving || !editingName.trim()}
                          >
                            {editSaving ? "…" : t("admin.clubs.saveEdit")}
                          </button>
                          <button style={s.cancelEditBtn} onClick={handleEditCancel}>
                            {t("admin.clubs.cancelEdit")}
                          </button>
                        </div>
                      ) : (
                        <div style={s.clubName}>{club.name}</div>
                      )}
                    </div>

                    {/* Athlete count badge */}
                    {clubRankingMap[club.name] !== undefined && (
                      <div style={{
                        backgroundColor: C.surface2,
                        border: `1px solid ${C.border}`,
                        borderRadius: "20px",
                        padding: "2px 8px",
                        fontSize: "11px",
                        color: C.text2,
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}>
                        🏃 {clubRankingMap[club.name]} ranked
                      </div>
                    )}

                    {canManageClubs && editingClubId !== club.id && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {/* Club avatar / image */}
                        {club.imageUrl ? (
                          <img
                            src={club.imageUrl}
                            style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }}
                            alt=""
                          />
                        ) : (
                          <div
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              backgroundColor: C.surface2,
                              border: `1px solid ${C.border}`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              color: C.text3,
                            }}
                          >
                            {club.name.charAt(0).toUpperCase()}
                          </div>
                        )}

                        {/* Hidden file input */}
                        <input
                          id={`club-logo-${club.id}`}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              const url = await uploadClubImage(club.id, file);
                              await updateClubImageUrl(club.id, url);
                              await loadClubs();
                            } catch (err) {
                              setError("Failed to upload image");
                            }
                          }}
                        />
                        <label
                          htmlFor={`club-logo-${club.id}`}
                          style={{ fontSize: "12px", cursor: "pointer", color: C.text3 }}
                        >
                          📷
                        </label>

                        {/* Edit button */}
                        <button
                          style={s.editBtn}
                          onClick={() => handleEditStart(club)}
                        >
                          {t("admin.clubs.editButton")}
                        </button>

                        <button
                          style={s.manageBtn}
                          onClick={() => navigate(`/admin/affiliations?tab=memberships&legacyClub=${encodeURIComponent(club.name)}`)}
                        >
                          {t("admin.clubs.manageMemberships")}
                        </button>
                        <button
                          style={s.manageBtn}
                          onClick={() => navigate(`/admin/affiliations?tab=coachAccess&legacyClub=${encodeURIComponent(club.name)}`)}
                        >
                          {t("admin.clubs.manageCoaches")}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Duplicate warning */}
        {hasDuplicates && (
          <div style={{
            backgroundColor: C.warningDim,
            border: `1px solid ${C.warningBorder}`,
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "12px",
            fontSize: "13px",
            color: "#856404",
            fontWeight: 600,
          }}>
            ⚠️ Duplicate club names detected: {[...new Set(duplicateNames)].join(", ")}
          </div>
        )}


      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1100px", margin: "0 auto", padding: "40px 24px" },
  header: { marginBottom: "28px" },
  pageLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "4px",
  },
  title: {
    fontFamily: FONT.display,
    fontSize: "36px",
    letterSpacing: "1px",
    color: C.text,
    marginBottom: "4px",
    lineHeight: 1,
  },
  subtitle: { color: C.text2, fontSize: "14px" },
  card: {
    backgroundColor: C.surface,
    borderRadius: "12px",
    border: `1px solid ${C.border}`,
    marginBottom: "20px",
    overflow: "hidden",
  },
  cardAccent: { height: "3px", background: `linear-gradient(90deg, ${C.red}, rgba(218,41,28,0.2))` },
  cardBody: { padding: "24px" },
  cardTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: C.text,
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  clubCount: {
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "20px",
    padding: "1px 8px",
    fontSize: "12px",
    color: C.text2,
    fontWeight: 600,
  },
  addForm: { display: "flex", gap: "12px" },
  input: {
    flex: 1,
    padding: "10px 14px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "14px",
    outline: "none",
    color: C.text,
    backgroundColor: C.surface2,
    transition: "border-color 0.15s, box-shadow 0.15s",
  },
  inputFocused: { borderColor: C.red, boxShadow: `0 0 0 3px ${C.redDim}` },
  primaryBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  errorMsg: {
    marginTop: "12px",
    backgroundColor: C.redDim,
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "7px",
    padding: "10px 14px",
    fontSize: "13px",
    color: C.red,
  },
  readOnlyNotice: {
    marginBottom: "16px",
    padding: "12px 14px",
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    background: C.surface,
    color: C.text2,
    fontSize: "13px",
  },
  stateMsg: { color: C.text2, fontSize: "14px", textAlign: "center", padding: "20px 0" },
  emptyState: { textAlign: "center", padding: "40px 0", color: C.text2, fontSize: "14px" },
  emptyIcon: { fontSize: "32px", marginBottom: "12px" },
  list: { display: "flex", flexDirection: "column", gap: "6px" },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    backgroundColor: C.surface2,
    borderRadius: "8px",
    border: `1px solid ${C.border}`,
    minHeight: "52px",
    flexWrap: "wrap",
  },
  listItemLeft: { display: "flex", alignItems: "center", gap: "12px", flex: 1, minWidth: 0 },
  listIndex: { fontFamily: "monospace", fontSize: "11px", color: C.text3, minWidth: "20px", flexShrink: 0 },
  clubName: { fontSize: "14px", color: C.text, fontWeight: 500 },
  // Inline edit
  editRow: { display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 },
  editInput: {
    flex: 1,
    padding: "6px 10px",
    border: `1px solid ${C.red}`,
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    color: C.text,
    backgroundColor: C.surface,
    boxShadow: `0 0 0 2px ${C.redDim}`,
    minWidth: 0,
  },
  saveEditBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 700,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  cancelEditBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 500,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  editBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 500,
  },
  manageBtn: {
    backgroundColor: C.surface,
    color: C.red,
    border: `1px solid ${C.red}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 650,
    whiteSpace: "nowrap",
  },
  renameNotice: {
    marginBottom: "14px",
    border: `1px solid ${C.border}`,
    borderRadius: "8px",
    padding: "11px 14px",
    backgroundColor: C.surface,
    color: C.text2,
    fontSize: "12px",
  },
  // Propagation
  propagateBanner: {
    backgroundColor: C.warningDim,
    border: `1px solid ${C.warning}`,
    borderRadius: "8px",
    padding: "16px",
    marginBottom: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  propagateBtn: {
    backgroundColor: C.warning,
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    alignSelf: "flex-start",
  },
  propagateSuccess: {
    backgroundColor: "var(--color-success-soft)",
    border: "1px solid #6ee7b7",
    borderRadius: "7px",
    padding: "12px 16px",
    fontSize: "13px",
    color: "var(--color-success)",
    fontWeight: 600,
    marginBottom: "16px",
    lineHeight: 1.6,
  },
};

export default ManageClubs;
